import React, { useState, useEffect } from 'react';
import BaseModal from './common/BaseModal';
import { API_URL } from '../config/api';

interface HashtagCount {
  hashtag: string;
  count: number;
  isExisting: boolean;
}

interface HashtagAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  posts: any[];
  onKeywordsAdded?: () => void;
}

const HashtagAnalysisModal: React.FC<HashtagAnalysisModalProps> = ({
  isOpen,
  onClose,
  posts,
  onKeywordsAdded
}) => {
  const [hashtagCounts, setHashtagCounts] = useState<HashtagCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingKeywords, setAddingKeywords] = useState<Set<string>>(new Set());
  const [addedKeywords, setAddedKeywords] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && posts.length > 0) {
      analyzeHashtags();
    }
  }, [isOpen, posts]);

  const analyzeHashtags = async () => {
    setLoading(true);

    // Extract all hashtags from posts
    const hashtagMap = new Map<string, number>();

    posts.forEach(post => {
      // Extract from itemInfos.text (main text field)
      const text = post.itemInfos?.text || post.text || post.description || '';
      const hashtagRegex = /#(\w+)/g;
      let match;
      while ((match = hashtagRegex.exec(text)) !== null) {
        const hashtagName = match[1].toLowerCase();
        if (hashtagName) {
          hashtagMap.set(hashtagName, (hashtagMap.get(hashtagName) || 0) + 1);
        }
      }

      // Extract from challengeInfoList (structured challenge/hashtag data)
      const challenges = post.challengeInfoList || post.challenges || [];
      challenges.forEach((challenge: any) => {
        const challengeName = (challenge.title || challenge.challengeName || challenge.name || '').toLowerCase().replace('#', '');
        if (challengeName) {
          hashtagMap.set(challengeName, (hashtagMap.get(challengeName) || 0) + 1);
        }
      });

      // Also check hashtags field if exists
      const hashtags = post.hashtags || [];
      hashtags.forEach((tag: any) => {
        const hashtagName = (tag.name || tag.hashtag_name || tag.title || '').toLowerCase().replace('#', '');
        if (hashtagName) {
          hashtagMap.set(hashtagName, (hashtagMap.get(hashtagName) || 0) + 1);
        }
      });
    });

    console.log('📊 Hashtag analysis:', {
      totalPosts: posts.length,
      uniqueHashtags: hashtagMap.size,
      hashtagCounts: Array.from(hashtagMap.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10)
    });

    // Filter hashtags mentioned 3+ times
    const filteredHashtags = Array.from(hashtagMap.entries())
      .filter(([_, count]) => count >= 3)
      .map(([hashtag, count]) => ({ hashtag, count, isExisting: false }))
      .sort((a, b) => b.count - a.count);

    // Check which hashtags already exist in the database
    try {
      const response = await fetch(`${API_URL}/api/keywords/check-existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywords: filteredHashtags.map(h => h.hashtag)
        }),
      });

      const data = await response.json();

      if (data.success && data.existingKeywords) {
        const existingSet = new Set(data.existingKeywords);
        filteredHashtags.forEach(item => {
          item.isExisting = existingSet.has(item.hashtag);
        });
      }
    } catch (err) {
      console.error('Error checking existing keywords:', err);
    }

    setHashtagCounts(filteredHashtags);
    setLoading(false);
  };

  const addKeyword = async (hashtag: string) => {
    setAddingKeywords(prev => new Set(prev).add(hashtag));

    try {
      const response = await fetch(`${API_URL}/api/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keyword: hashtag.toLowerCase().replace(/\s+/g, '')
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAddedKeywords(prev => new Set(prev).add(hashtag));
        setHashtagCounts(prev =>
          prev.map(h =>
            h.hashtag === hashtag
              ? { ...h, isExisting: true }
              : h
          )
        );
      } else {
        console.error('Failed to add keyword:', data.error);
      }
    } catch (err) {
      console.error('Error adding keyword:', err);
    } finally {
      setAddingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(hashtag);
        return newSet;
      });
    }
  };

  const addAllNewKeywords = async () => {
    const newHashtags = hashtagCounts.filter(h => !h.isExisting && !addedKeywords.has(h.hashtag));

    if (newHashtags.length === 0) {
      return;
    }

    const hashtagsToAdd = newHashtags.map(h => h.hashtag);
    setAddingKeywords(new Set(hashtagsToAdd));

    // Add keywords in batches
    const batchSize = 5;
    for (let i = 0; i < newHashtags.length; i += batchSize) {
      const batch = newHashtags.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (item) => {
          try {
            const response = await fetch(`${API_URL}/api/keywords`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                keyword: item.hashtag.toLowerCase().replace(/\s+/g, '')
              }),
            });

            const data = await response.json();

            if (data.success) {
              setAddedKeywords(prev => new Set(prev).add(item.hashtag));
              setHashtagCounts(prev =>
                prev.map(h =>
                  h.hashtag === item.hashtag
                    ? { ...h, isExisting: true }
                    : h
                )
              );
            }
          } catch (err) {
            console.error(`Error adding keyword ${item.hashtag}:`, err);
          } finally {
            setAddingKeywords(prev => {
              const newSet = new Set(prev);
              newSet.delete(item.hashtag);
              return newSet;
            });
          }
        })
      );
    }

    if (onKeywordsAdded) {
      onKeywordsAdded();
    }
  };

  const totalHashtags = hashtagCounts.length;
  const newHashtags = hashtagCounts.filter(h => !h.isExisting).length;
  const existingHashtags = hashtagCounts.filter(h => h.isExisting).length;

  return (
    <BaseModal
      show={isOpen}
      onClose={onClose}
      title="Hashtag Analysis"
      subtitle={`Analyzing ${posts.length} posts`}
      size="large"
      headerGradient={false}
      showHeader={true}
    >
      <div style={{ padding: '32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
            <p>Analyzing hashtags...</p>
          </div>
        ) : (
          <>
            {/* Stats Bar */}
            {totalHashtags > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f0f4ff',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  Found <strong>{totalHashtags}</strong> hashtags (mentioned 3+ times)
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                    {newHashtags} new · {existingHashtags} existing
                  </div>
                </div>
                {newHashtags > 0 && (
                  <button
                    onClick={addAllNewKeywords}
                    disabled={addingKeywords.size > 0}
                    className="btn btn-primary"
                    style={{
                      background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                      color: 'white'
                    }}
                  >
                    {addingKeywords.size > 0
                      ? `Adding ${addingKeywords.size} keywords...`
                      : `Add All ${newHashtags} New Keywords`}
                  </button>
                )}
              </div>
            )}

            {/* Hashtags Table */}
            {totalHashtags > 0 ? (
              <div className="table-container" style={{ maxHeight: '500px', overflow: 'auto' }}>
                <table className="table">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '60%' }}>Hashtag</th>
                      <th style={{ width: '20%' }}>Mentions</th>
                      <th style={{ width: '20%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hashtagCounts.map((item) => (
                      <tr key={item.hashtag}>
                        <td>
                          <span style={{ fontWeight: 500 }}>
                            #{item.hashtag}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            fontWeight: 'bold',
                            color: item.count >= 10 ? '#51cf66' :
                                   item.count >= 5 ? '#ffd43b' :
                                   '#868e96'
                          }}>
                            {item.count}x
                          </span>
                        </td>
                        <td>
                          {item.isExisting || addedKeywords.has(item.hashtag) ? (
                            <span style={{
                              color: '#868e96',
                              fontSize: '14px'
                            }}>
                              ✓ Already Added
                            </span>
                          ) : (
                            <button
                              onClick={() => addKeyword(item.hashtag)}
                              disabled={addingKeywords.has(item.hashtag)}
                              className="btn btn-secondary"
                              style={{
                                padding: '4px 12px',
                                fontSize: '12px'
                              }}
                            >
                              {addingKeywords.has(item.hashtag)
                                ? 'Adding...'
                                : 'Add Keyword'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No hashtags found with 3+ mentions</p>
              </div>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default HashtagAnalysisModal;
